import { useId } from 'react';
import { Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Toggle } from '@amzn/gsce-shared-react-lib/components';

interface BaseProps {
  /** Whether the toggle is on (controlled). */
  readonly checked: boolean;
  /** Fired with the new on/off state. */
  readonly onChange: (checked: boolean) => void;
  /**
   * Tracking id for the shared `Toggle` (required by the lib — logged on
   * every change). Callers pass a stable, field-scoped id, e.g.
   * `audit-whiz.review.q-config.require-comment-on-fail`.
   */
  readonly trackingId: string;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * Standalone mode: the field renders its own visible label beside the switch
 * (used by the Sub Questions step, where toggles sit in a plain row).
 */
interface WithOwnLabel extends BaseProps {
  readonly label: string;
  readonly labelId?: undefined;
}

/**
 * External-label mode: the visible label + info tooltip live in a surrounding
 * {@link ConfigField} (the Question Configuration step, matching the manual
 * flow's label-on-top with info icon); the field renders the switch only,
 * wired to that label via `aria-labelledby`.
 */
interface WithExternalLabel extends BaseProps {
  readonly label?: undefined;
  readonly labelId: string;
}

export type ReviewQuestionToggleFieldProps = WithOwnLabel | WithExternalLabel;

/**
 * A boolean switch for the review wizard's requirement toggles
 * (Comment/Attachment Required on Fail/Pass), used at the question level
 * (Question Configuration step) and per sub-question (Sub Questions step).
 *
 * Two labelling modes: pass `label` to render a self-owned label beside the
 * switch, or pass `labelId` to render the switch alone wired to an external
 * label (e.g. a {@link ConfigField}). Wraps the shared `Toggle` (a tracked
 * pill switch over Stencil's `ToggleSwitch`). Controlled — the owning step
 * holds state.
 */
export function ReviewQuestionToggleField(
  props: ReviewQuestionToggleFieldProps,
): JSX.Element {
  const { checked, onChange, trackingId, disabled = false, dataTestId } = props;

  // Own-label mode needs an id to connect its Text label to the switch.
  const ownLabelId = useId();
  const switchLabelId = props.label !== undefined ? ownLabelId : props.labelId;

  const toggle = (
    <Toggle
      trackingId={trackingId}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      labelId={switchLabelId}
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    />
  );

  // External-label mode: render the switch only.
  if (props.label === undefined) {
    return toggle;
  }

  // Own-label mode: label beside the switch.
  return (
    <Row alignItems="center" gridGap="dimensions.spacing.100">
      {toggle}
      <Text id={ownLabelId}>{props.label}</Text>
    </Row>
  );
}
